import { Link, useMatchRoute, type LinkOptions } from '@tanstack/react-router';
import React from 'react';

import { useSettings } from '@/stores/settings';

import Emoji from '../ui/emoji';
import Icon from '../ui/icon';

interface ISidebarNavigationLink extends Partial<LinkOptions> {
  /** Notification count, if any. */
  count?: number;
  /** Optional max to cap count (ie: N+) */
  countMax?: number;
  /** URL to an SVG icon. */
  icon: string;
  /** URL to an SVG icon for active state. */
  activeIcon?: string;
  emoji?: string;
  emojiUrl?: string;
  /** Link label. */
  text: React.ReactNode;
  /** Whether only the exact route is active. */
  exact?: boolean;
  /** Callback when the link is clicked. */
  onClick?: React.EventHandler<React.MouseEvent>;
  rel?: string;
}

/** Desktop sidebar navigation link. */
const SidebarNavigationLink: React.FC<ISidebarNavigationLink> = React.memo(
  React.forwardRef((props, ref: React.ForwardedRef<HTMLAnchorElement>): React.JSX.Element => {
    const {
      icon,
      activeIcon,
      emoji,
      emojiUrl,
      text,
      to,
      count,
      countMax,
      onClick,
      exact = true,
      ...rest
    } = props;

    const matchRoute = useMatchRoute();
    const { demetricator } = useSettings();

    const LinkComponent = (rest.href ? 'a' : to === undefined ? 'button' : Link) as typeof Link;
    const isRouterLink = LinkComponent === Link;

    if (rest.href) {
      rest.target = '_blank';
      rest.rel = 'noopener noreferrer';
    }

    const isActive = matchRoute({ to, ...rest, fuzzy: !exact }) !== false;

    const activeLinkProps = isRouterLink
      ? {
          activeOptions: { exact, includeSearch: false },
          activeProps: { className: 'sidebar-navigation-link--active' },
        }
      : {};

    return (
      <LinkComponent
        {...activeLinkProps}
        to={to}
        ref={ref}
        onClick={onClick}
        className='sidebar-navigation-link'
        {...rest}
      >
        <span className='sidebar-navigation-link__icon' aria-hidden>
          {emoji || emojiUrl ? (
            <Emoji emoji={emoji} src={emojiUrl} alt='' />
          ) : (
            <Icon
              src={(isActive && activeIcon) || icon}
              count={demetricator !== 'off' ? undefined : count}
              countMax={countMax}
            />
          )}
        </span>

        <p>{text}</p>
      </LinkComponent>
    );
  }),
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text &&
    prevProps.count === nextProps.count &&
    prevProps.emoji === nextProps.emoji &&
    prevProps.emojiUrl === nextProps.emojiUrl,
);

export { SidebarNavigationLink as default };
